import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Paper,
  IconButton,
  Switch,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Divider
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Brush as BrushIcon,
  FormatSize as FontIcon,
  ViewModule as LayoutIcon,
  Devices as DevicesIcon,
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  ColorLens as ColorIcon,
  TextFields as TextIcon,
  GridOn as GridIcon,
  Image as ImageIcon,
  AutoAwesome as MagicIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckIcon,
  Tune as TuneIcon,
  Visibility as PreviewIcon,
  Code as CodeIcon,
  Style as StyleIcon,
  Gradient as GradientIcon,
  Animation as AnimationIcon,
  TouchApp as TouchIcon,
  DesignServices
} from '@mui/icons-material';

const ThemeDesignSystemPage = () => {
  const [themeMode, setThemeMode] = useState('light');
  const [primaryColor, setPrimaryColor] = useState('#ff6b35');
  const [hoveredTheme, setHoveredTheme] = useState(null);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [activeDemo, setActiveDemo] = useState(0);

  // Hazır Tema Şablonları
  const themeTemplates = [
    {
      id: 'modern',
      name: 'Modern Haber',
      preview: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&h=300&fit=crop',
      primaryColor: '#3b82f6',
      secondaryColor: '#1d4ed8',
      style: 'Minimalist, temiz çizgiler',
      bestFor: 'Teknoloji haberleri',
      colors: ['#3b82f6', '#1d4ed8', '#60a5fa', '#93c5fd']
    },
    {
      id: 'classic',
      name: 'Klasik Gazete',
      preview: 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=400&h=300&fit=crop',
      primaryColor: '#1a1a2e',
      secondaryColor: '#dc2626',
      style: 'Geleneksel, güçlü tipografi',
      bestFor: 'Ulusal haberler',
      colors: ['#1a1a2e', '#dc2626', '#495057', '#6c757d']
    },
    {
      id: 'magazine',
      name: 'Magazin Stili',
      preview: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=300&fit=crop',
      primaryColor: '#ec4899',
      secondaryColor: '#be185d',
      style: 'Renkli, dinamik, görsel ağırlıklı',
      bestFor: 'Lifestyle ve magazin',
      colors: ['#ec4899', '#be185d', '#f472b6', '#fbcfe8']
    },
    {
      id: 'business',
      name: 'İş Dünyası',
      preview: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop',
      primaryColor: '#0891b2',
      secondaryColor: '#0e7490',
      style: 'Profesyonel, kurumsal',
      bestFor: 'Ekonomi haberleri',
      colors: ['#0891b2', '#0e7490', '#06b6d4', '#67e8f9']
    },
    {
      id: 'sports',
      name: 'Spor Teması',
      preview: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=400&h=300&fit=crop',
      primaryColor: '#10b981',
      secondaryColor: '#047857',
      style: 'Enerjik, dinamik, bold',
      bestFor: 'Spor haberleri',
      colors: ['#10b981', '#047857', '#34d399', '#6ee7b7']
    },
    {
      id: 'dark',
      name: 'Koyu Tema',
      preview: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=400&h=300&fit=crop',
      primaryColor: '#8b5cf6',
      secondaryColor: '#7c3aed',
      style: 'Modern, göz yormayan',
      bestFor: 'Gece okuma modu',
      colors: ['#8b5cf6', '#7c3aed', '#a78bfa', '#c4b5fd']
    }
  ];

  // Özelleştirme Özellikleri
  const customizationFeatures = [
    {
      icon: <ColorIcon />,
      title: 'Renk Paleti',
      description: 'Sınırsız renk kombinasyonu',
      color: '#3b82f6',
      details: ['Birincil renk', 'İkincil renk', 'Accent renkler', 'Gradient oluşturucu']
    },
    {
      icon: <TextIcon />,
      title: 'Tipografi',
      description: 'Google Fonts entegrasyonu',
      color: '#10b981',
      details: ['800+ font ailesi', 'Font boyutları', 'Satır aralıkları', 'Font ağırlıkları']
    },
    {
      icon: <LayoutIcon />,
      title: 'Layout Sistemi',
      description: 'Esnek düzen seçenekleri',
      color: '#f59e0b',
      details: ['Grid sistemleri', 'Spacing ayarları', 'Container genişlikleri', 'Breakpoint\'ler']
    },
    {
      icon: <BrushIcon />,
      title: 'Stil Özelleştirme',
      description: 'Detaylı stil kontrolü',
      color: '#8b5cf6',
      details: ['Border radius', 'Gölge efektleri', 'Hover animasyonları', 'Transition\'lar']
    },
    {
      icon: <ImageIcon />,
      title: 'Logo & Marka',
      description: 'Marka kimliği yönetimi',
      color: '#ec4899',
      details: ['Logo yükleme', 'Favicon', 'Marka renkleri', 'Watermark']
    },
    {
      icon: <DevicesIcon />,
      title: 'Responsive Ayarlar',
      description: 'Cihaz bazlı özelleştirme',
      color: '#06b6d4',
      details: ['Mobil ayarları', 'Tablet görünümü', 'Desktop düzeni', 'Breakpoint kontrolü']
    }
  ];

  // Tema Özellikleri
  const themeCapabilities = [
    {
      title: 'Açık/Koyu Mod',
      icon: <DarkIcon />,
      description: 'Otomatik veya manuel tema değiştirme',
      color: '#3b82f6'
    },
    {
      title: 'Canlı Önizleme',
      icon: <PreviewIcon />,
      description: 'Değişiklikleri anında görün',
      color: '#10b981'
    },
    {
      title: 'Tema Export/Import',
      icon: <CodeIcon />,
      description: 'Temalarınızı kaydedin ve paylaşın',
      color: '#f59e0b'
    },
    {
      title: 'CSS Variables',
      icon: <StyleIcon />,
      description: 'Modern CSS değişkenleri desteği',
      color: '#8b5cf6'
    },
    {
      title: 'Gradient Builder',
      icon: <GradientIcon />,
      description: 'Özel gradient oluşturucu',
      color: '#ec4899'
    },
    {
      title: 'Animation Control',
      icon: <AnimationIcon />,
      description: 'Animasyon hızı ve efekt ayarları',
      color: '#06b6d4'
    }
  ];

  // Demo Temaları (Carousel için)
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveDemo((prev) => (prev + 1) % themeTemplates.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
      
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
          pt: { xs: 12, md: 16 },
          pb: { xs: 10, md: 15 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Animated Color Blobs */}
        <Box
          sx={{
            position: 'absolute',
            top: -150,
            right: -150,
            width: 500,
            height: 500,
            background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            opacity: 0.15,
            filter: 'blur(80px)',
            animation: 'morph 10s ease-in-out infinite'
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            bottom: -100,
            left: -100,
            width: 400,
            height: 400,
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
            opacity: 0.15,
            filter: 'blur(80px)',
            animation: 'morph 12s ease-in-out infinite reverse'
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 200,
            left: 100,
            width: 300,
            height: 300,
            background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
            borderRadius: '50% 50% 30% 70% / 50% 30% 70% 40%',
            opacity: 0.1,
            filter: 'blur(60px)',
            animation: 'morph 15s ease-in-out infinite'
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                label="TEMA & TASARIM SİSTEMİ"
                icon={<PaletteIcon sx={{ fontSize: '1rem' }} />}
                sx={{
                  background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.2), rgba(247, 147, 30, 0.2))',
                  color: '#ff6b35',
                  border: '1px solid rgba(255, 107, 53, 0.3)',
                  backdropFilter: 'blur(10px)',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  mb: 3,
                  px: 2,
                  py: 2.5
                }}
              />
              
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '4.5rem' },
                  fontWeight: 900,
                  color: 'white',
                  mb: 3,
                  lineHeight: 1.1
                }}
              >
                Sınırsız{' '}
                <span style={{ 
                  background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #3b82f6 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Tasarım Özgürlüğü
                </span>
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: { xs: '1.1rem', md: '1.4rem' },
                  lineHeight: 1.6,
                  mb: 5
                }}
              >
                Marka kimliğinize uygun, tamamen özelleştirilebilir tema sistemi. 
                Kod yazmadan profesyonel tasarımlar oluşturun.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} mb={5}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                    color: 'white',
                    px: 5,
                    py: 2.5,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: '50px',
                    textTransform: 'none',
                    boxShadow: '0 8px 32px rgba(255, 107, 53, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #f7931e 0%, #ff6b35 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px rgba(255, 107, 53, 0.5)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Tema Editörünü Dene
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'white',
                    px: 5,
                    py: 2.5,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: '50px',
                    textTransform: 'none',
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: '#ff6b35',
                      color: '#ff6b35',
                      background: 'rgba(255, 107, 53, 0.1)',
                      borderWidth: 2
                    }
                  }}
                >
                  Şablonları İncele
                </Button>
              </Stack>

              {/* Quick Features */}
              <Grid container spacing={2}>
                {[
                  { icon: <PaletteIcon />, label: 'Sınırsız Renk', color: '#ff6b35' },
                  { icon: <FontIcon />, label: '800+ Font', color: '#3b82f6' },
                  { icon: <TouchIcon />, label: 'Canlı Önizleme', color: '#10b981' }
                ].map((item, idx) => (
                  <Grid item xs={4} key={idx}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        background: 'rgba(255,255,255,0.05)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'rgba(255,255,255,0.1)',
                          transform: 'translateY(-4px)'
                        }
                      }}
                    >
                      <Box sx={{ color: item.color, fontSize: '1.8rem', mb: 1 }}>
                        {item.icon}
                      </Box>
                      <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.85rem' }}>
                        {item.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Live Theme Customizer Mockup */}
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
                  border: '2px solid rgba(255,255,255,0.1)'
                }}
              >
                {/* Browser Bar */}
                <Box
                  sx={{
                    background: 'rgba(255,255,255,0.95)',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.8 }}>
                    {['#ff5f56', '#ffbd2e', '#27c93f'].map((color, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          background: color
                        }}
                      />
                    ))}
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      ml: 2,
                      background: '#f1f3f5',
                      borderRadius: '50px',
                      px: 2,
                      py: 0.5
                    }}
                  >
                    <Typography sx={{ fontSize: '0.8rem', color: '#6c757d' }}>
                      🎨 Tema Editörü - Canlı Önizleme
                    </Typography>
                  </Box>
                </Box>

                {/* Theme Preview */}
                <Box
                  sx={{
                    background: themeMode === 'light' ? 'white' : '#1a1a2e',
                    p: 4,
                    minHeight: 500,
                    position: 'relative',
                    transition: 'all 0.5s ease'
                  }}
                >
                  {/* Animated Theme Preview */}
                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: 3,
                      overflow: 'hidden',
                      mb: 3,
                      transition: 'all 0.5s ease'
                    }}
                  >
                    <img
                      src={themeTemplates[activeDemo].preview}
                      alt="Theme Preview"
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        borderRadius: '12px'
                      }}
                    />

                    {/* Color Palette Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 15,
                        left: 15,
                        right: 15,
                        background: 'rgba(255,255,255,0.95)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 2,
                        p: 2,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a2e', mb: 1 }}>
                        {themeTemplates[activeDemo].name}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {themeTemplates[activeDemo].colors.map((color, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1,
                              background: color,
                              border: '2px solid white',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease',
                              '&:hover': {
                                transform: 'scale(1.2)'
                              }
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Box>

                  {/* Theme Controls */}
                  <Stack spacing={2}>
                    {/* Dark Mode Toggle */}
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            background: themeMode === 'dark' ? '#1a1a2e' : '#f59e0b',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {themeMode === 'dark' ? <DarkIcon /> : <LightIcon />}
                        </Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: themeMode === 'light' ? '#1a1a2e' : 'white' }}>
                          {themeMode === 'dark' ? 'Koyu Mod' : 'Açık Mod'}
                        </Typography>
                      </Stack>
                      <Switch
                        checked={themeMode === 'dark'}
                        onChange={(e) => setThemeMode(e.target.checked ? 'dark' : 'light')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#1a1a2e'
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#1a1a2e'
                          }
                        }}
                      />
                    </Paper>

                    {/* Color Picker */}
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: themeMode === 'light' ? '#1a1a2e' : 'white', mb: 2 }}>
                        Birincil Renk
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {['#ff6b35', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899'].map((color, idx) => (
                          <Box
                            key={idx}
                            onClick={() => setPrimaryColor(color)}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              background: color,
                              cursor: 'pointer',
                              border: primaryColor === color ? '3px solid white' : 'none',
                              boxShadow: primaryColor === color ? `0 0 0 2px ${color}` : 'none',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                transform: 'scale(1.15)'
                              }
                            }}
                          />
                        ))}
                      </Stack>
                    </Paper>
                  </Stack>

                  {/* Live Indicator */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      right: 20,
                      background: 'rgba(16, 185, 129, 0.9)',
                      color: 'white',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      animation: 'pulse 2s ease-in-out infinite'
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'white',
                        animation: 'pulse 1s ease-in-out infinite'
                      }}
                    />
                    CANLI ÖNİZLEME
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* Dalga Geçişi */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            overflow: 'hidden',
            lineHeight: 0
          }}
        >
          <svg
            viewBox="0 0 1440 400"
            preserveAspectRatio="none"
            style={{ width: '100%', height: '250px', display: 'block' }}
          >
            <path
              d="M0,160L48,170.7C96,181,192,203,288,197.3C384,192,480,160,576,154.7C672,149,768,171,864,181.3C960,192,1056,192,1152,176C1248,160,1344,128,1392,112L1440,96L1440,400L0,400Z"
              fill="#f8f9fa"
            />
          </svg>
        </Box>
      </Box>

      {/* Hazır Tema Şablonları */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          py: { xs: 8, md: 12 }}
        }
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: '#ff6b35',
                fontSize: '0.9rem',
                fontWeight: 700,
                letterSpacing: '0.1em'
              }}
            >
              HAZIR ŞABLONLAR
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                color: '#1a1a2e',
                mb: 2,
                mt: 2
              }}
            >
              6 Farklı Tema Şablonu
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#6c757d',
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              Hazır temalarla başlayın, istediğiniz gibi özelleştirin
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {themeTemplates.map((theme, index) => (
              <Grid item xs={12} sm={6} md={4} key={theme.id}>
                <Card
                  onMouseEnter={() => setHoveredTheme(index)}
                  onMouseLeave={() => setHoveredTheme(null)}
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: '2px solid #e2e8f0',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.4s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-12px)',
                      boxShadow: `0 24px 48px ${theme.primaryColor}30`,
                      borderColor: theme.primaryColor,
                      '& .theme-preview': {
                        transform: 'scale(1.1)'
                      },
                      '& .theme-overlay': {
                        opacity: 1
                      }
                    }
                  }}
                >
                  {/* Preview Image */}
                  <Box
                    sx={{
                      position: 'relative',
                      height: 250,
                      overflow: 'hidden',
                      background: '#f8f9fa'
                    }}
                  >
                    <img
                      className="theme-preview"
                      src={theme.preview}
                      alt={theme.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease'
                      }}
                    />

                    {/* Hover Overlay */}
                    <Box
                      className="theme-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.7)',
                        backdropFilter: 'blur(5px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        opacity: 0,
                        transition: 'opacity 0.3s ease'
                      }}
                    >
                      <Button
                        variant="contained"
                        startIcon={<PreviewIcon />}
                        sx={{
                          background: 'white',
                          color: theme.primaryColor,
                          fontWeight: 600,
                          borderRadius: '50px',
                          px: 3,
                          '&:hover': {
                            background: '#f8f9fa'
                          }
                        }}
                      >
                        Önizle
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<CheckIcon />}
                        sx={{
                          background: theme.primaryColor,
                          color: 'white',
                          fontWeight: 600,
                          borderRadius: '50px',
                          px: 3,
                          '&:hover': {
                            background: theme.primaryColor,
                            filter: 'brightness(1.1)'
                          }
                        }}
                      >
                        Kullan
                      </Button>
                    </Box>

                    {/* Color Palette */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 15,
                        right: 15,
                        display: 'flex',
                        gap: 0.5,
                        background: 'rgba(255,255,255,0.95)',
                        backdropFilter: 'blur(10px)',
                        p: 1,
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    >
                      {theme.colors.slice(0, 4).map((color, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            background: color,
                            border: '2px solid white'
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    {/* Title & Badge */}
                    <Stack direction="row" justifyContent="space-between" alignItems="start" mb={2}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: '#1a1a2e'
                        }}
                      >
                        {theme.name}
                      </Typography>
                      <Chip
                        label={theme.style.split(',')[0]}
                        size="small"
                        sx={{
                          background: `${theme.primaryColor}15`,
                          color: theme.primaryColor,
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }}
                      />
                    </Stack>

                    {/* Description */}
                    <Typography
                      sx={{
                        color: '#6c757d',
                        fontSize: '0.9rem',
                        lineHeight: 1.6,
                        mb: 2
                      }}
                    >
                      {theme.style}
                    </Typography>

                    {/* Best For */}
                    <Paper
                      sx={{
                        p: 1.5,
                        background: '#f8f9fa',
                        borderRadius: 2,
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckIcon sx={{ color: theme.primaryColor, fontSize: '1rem' }} />
                        <Typography sx={{ fontSize: '0.85rem', color: '#495057' }}>
                          <strong>En İyi:</strong> {theme.bestFor}
                        </Typography>
                      </Stack>
                    </Paper>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Tema Sayacı */}
          <Box
            sx={{
              mt: 6,
              p: 4,
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.05), rgba(247, 147, 30, 0.05))',
              border: '2px dashed #ff6b35',
              textAlign: 'center'
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 800,
                color: '#ff6b35',
                mb: 1
              }}
            >
              6 Hazır Tema + Sınırsız Özelleştirme
            </Typography>
            <Typography
              sx={{
                color: '#6c757d',
                fontSize: '1.1rem'
              }}
            >
              İstediğiniz temayı seçin veya sıfırdan oluşturun
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Header & Footer Designer Bölümü - YENİ EKLEME */}
<Box
  sx={{
    background: 'white',
    py: { xs: 8, md: 12 },
    position: 'relative',
    overflow: 'hidden'
  }}
>
  {/* Dekoratif Pattern */}
  <Box
    sx={{
      position: 'absolute',
      top: 50,
      right: 50,
      width: 200,
      height: 200,
      backgroundImage: 'radial-gradient(circle, rgba(236, 72, 153, 0.1) 2px, transparent 2px)',
      backgroundSize: '20px 20px'
    }}
  />

  <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
    <Box sx={{ textAlign: 'center', mb: 8 }}>
      <Typography
        variant="overline"
        sx={{
          color: '#ec4899',
          fontSize: '0.9rem',
          fontWeight: 700,
          letterSpacing: '0.1em'
        }}
      >
        HEADER & FOOTER TASARIMCISI
      </Typography>
      <Typography
        variant="h2"
        sx={{
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          fontWeight: 800,
          color: '#1a1a2e',
          mb: 2,
          mt: 2
        }}
      >
        Başlık ve Alt Bilgi{' '}
        <span style={{ color: '#ec4899' }}>Özelleştirme</span>
      </Typography>
      <Typography
        variant="h6"
        sx={{
          color: '#6c757d',
          maxWidth: 700,
          mx: 'auto'
        }}
      >
        Sürükle-bırak ile header ve footer tasarlayın. 
        Logo, menü, sosyal medya ve daha fazlası.
      </Typography>
    </Box>

    <Grid container spacing={6}>
      {/* Header Designer */}
      <Grid item xs={12} md={6}>
        <Card
          sx={{
            height: '100%',
            borderRadius: 4,
            border: '2px solid #e2e8f0',
            overflow: 'hidden',
            transition: 'all 0.4s ease',
            '&:hover': {
              borderColor: '#ec4899',
              transform: 'translateY(-8px)',
              boxShadow: '0 20px 40px rgba(236, 72, 153, 0.2)',
              '& .designer-preview': {
                transform: 'scale(1.05)'
              }
            }
          }}
        >
          {/* Preview */}
          <Box
            sx={{
              position: 'relative',
              height: 350,
              overflow: 'hidden',
              background: '#f8f9fa'
            }}
          >
            <img
              className="designer-preview"
              src="https://images.unsplash.com/photo-1558655146-d09347e92766?w=700&h=400&fit=crop"
              alt="Header Designer"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.6s ease'
              }}
            />

            {/* Header Mockup Overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                p: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 800,
                      fontSize: '1.2rem'
                    }}
                  >
                    H
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e' }}>
                    Header Designer
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  {['Anasayfa', 'Haberler', 'Kategoriler'].map((item, idx) => (
                    <Chip
                      key={idx}
                      label={item}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        background: '#f1f3f5'
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            </Box>

            {/* Element Badges */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 20,
                left: 20,
                right: 20,
                display: 'flex',
                gap: 1.5,
                flexWrap: 'wrap'
              }}
            >
              {[
                { icon: '🎨', label: 'Logo', color: '#3b82f6' },
                { icon: '📋', label: 'Menü', color: '#10b981' },
                { icon: '🔍', label: 'Arama', color: '#f59e0b' },
                { icon: '👤', label: 'Kullanıcı', color: '#8b5cf6' }
              ].map((badge, idx) => (
                <Chip
                  key={idx}
                  icon={<span style={{ fontSize: '1rem' }}>{badge.icon}</span>}
                  label={badge.label}
                  sx={{
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(10px)',
                    color: badge.color,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: `2px solid ${badge.color}30`
                  }}
                />
              ))}
            </Box>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: '#1a1a2e',
                mb: 2
              }}
            >
              Header Tasarımcısı
            </Typography>

            <Typography
              sx={{
                color: '#6c757d',
                fontSize: '1rem',
                lineHeight: 1.7,
                mb: 3
              }}
            >
              Sürükle-bırak ile header tasarlayın. Logo, menü, arama, 
              kullanıcı profili ve daha fazlasını ekleyin.
            </Typography>

            {/* Özellikler */}
            <Stack spacing={2} mb={4}>
              {[
                { icon: '🎨', text: 'Logo & Marka Kimliği' },
                { icon: '📋', text: 'Dropdown & Mega Menü' },
                { icon: '🔍', text: 'Arama Kutusu' },
                { icon: '👤', text: 'Kullanıcı Profil Alanı' },
                { icon: '🌐', text: 'Dil Seçici' },
                { icon: '☀️', text: 'Hava Durumu Widget' }
              ].map((item, idx) => (
                <Stack key={idx} direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1rem'
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.9rem',
                      color: '#495057',
                      fontWeight: 500
                    }}
                  >
                    {item.text}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {/* Stats */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, color: '#ec4899' }}>
                    20+
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6c757d' }}>
                    Element Türü
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, color: '#ec4899' }}>
                    10+
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6c757d' }}>
                    Hazır Şablon
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, color: '#ec4899' }}>
                    ∞
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6c757d' }}>
                    Özelleştirme
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Button
              fullWidth
              variant="contained"
              endIcon={<ArrowIcon />}
              sx={{
                background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                color: 'white',
                py: 2,
                borderRadius: '50px',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: '0 8px 24px rgba(236, 72, 153, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #be185d 0%, #ec4899 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 32px rgba(236, 72, 153, 0.4)'
                }
              }}
            >
              Header Tasarımcısını Dene
            </Button>
          </CardContent>
        </Card>
      </Grid>

      {/* Footer Designer */}
      <Grid item xs={12} md={6}>
        <Card
          sx={{
            height: '100%',
            borderRadius: 4,
            border: '2px solid #e2e8f0',
            overflow: 'hidden',
            transition: 'all 0.4s ease',
            '&:hover': {
              borderColor: '#06b6d4',
              transform: 'translateY(-8px)',
              boxShadow: '0 20px 40px rgba(6, 182, 212, 0.2)',
              '& .designer-preview': {
                transform: 'scale(1.05)'
              }
            }
          }}
        >
          {/* Preview */}
          <Box
            sx={{
              position: 'relative',
              height: 350,
              overflow: 'hidden',
              background: '#f8f9fa'
            }}
          >
            <img
              className="designer-preview"
              src="https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=700&h=400&fit=crop"
              alt="Footer Designer"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.6s ease'
              }}
            />

            {/* Footer Mockup Overlay */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'rgba(26, 26, 46, 0.95)',
                backdropFilter: 'blur(10px)',
                p: 2.5,
                boxShadow: '0 -4px 12px rgba(0,0,0,0.2)'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.8rem', mb: 1 }}>
                    Hakkımızda
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}>
                    Site açıklaması...
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.8rem', mb: 1 }}>
                    Hızlı Linkler
                  </Typography>
                  <Stack spacing={0.5}>
                    {['İletişim', 'Gizlilik', 'Hakkımızda'].map((link, idx) => (
                      <Typography key={idx} sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}>
                        {link}
                      </Typography>
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.8rem', mb: 1 }}>
                    Sosyal Medya
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    {['📘', '🐦', '📷', '📺'].map((icon, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1,
                          background: 'rgba(255,255,255,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.8rem'
                        }}
                      >
                        {icon}
                      </Box>
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            {/* Element Badges */}
            <Box
              sx={{
                position: 'absolute',
                top: 20,
                left: 20,
                right: 20,
                display: 'flex',
                gap: 1.5,
                flexWrap: 'wrap'
              }}
            >
              {[
                { icon: '📝', label: 'Metin', color: '#3b82f6' },
                { icon: '🔗', label: 'Linkler', color: '#10b981' },
                { icon: '📱', label: 'Sosyal', color: '#f59e0b' },
                { icon: '📧', label: 'Newsletter', color: '#8b5cf6' }
              ].map((badge, idx) => (
                <Chip
                  key={idx}
                  icon={<span style={{ fontSize: '1rem' }}>{badge.icon}</span>}
                  label={badge.label}
                  sx={{
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(10px)',
                    color: badge.color,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: `2px solid ${badge.color}30`
                  }}
                />
              ))}
            </Box>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: '#1a1a2e',
                mb: 2
              }}
            >
              Footer Tasarımcısı
            </Typography>

            <Typography
              sx={{
                color: '#6c757d',
                fontSize: '1rem',
                lineHeight: 1.7,
                mb: 3
              }}
            >
              Alt bilgi bölümünü özelleştirin. İletişim bilgileri, 
              linkler, sosyal medya ve newsletter ekleyin.
            </Typography>

            {/* Özellikler */}
            <Stack spacing={2} mb={4}>
              {[
                { icon: '📝', text: 'Metin & Açıklama' },
                { icon: '🔗', text: 'Link Grupları' },
                { icon: '📱', text: 'Sosyal Medya İkonları' },
                { icon: '📧', text: 'Newsletter Formu' },
                { icon: '📞', text: 'İletişim Bilgileri' },
                { icon: '©️', text: 'Copyright Metni' }
              ].map((item, idx) => (
                <Stack key={idx} direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1rem'
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.9rem',
                      color: '#495057',
                      fontWeight: 500
                    }}
                  >
                    {item.text}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {/* Stats */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, color: '#06b6d4' }}>
                    15+
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6c757d' }}>
                    Element Türü
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, color: '#06b6d4' }}>
                    8+
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6c757d' }}>
                    Hazır Şablon
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, color: '#06b6d4' }}>
                    3
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6c757d' }}>
                    Kolon Düzeni
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Button
              fullWidth
              variant="contained"
              endIcon={<ArrowIcon />}
              sx={{
                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                color: 'white',
                py: 2,
                borderRadius: '50px',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: '0 8px 24px rgba(6, 182, 212, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 32px rgba(6, 182, 212, 0.4)'
                }
              }}
            >
              Footer Tasarımcısını Dene
            </Button>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    {/* Özellik Karşılaştırması */}
    <Box
      sx={{
        mt: 8,
        p: 5,
        borderRadius: 4,
        background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.05), rgba(6, 182, 212, 0.05))',
        border: '2px dashed #e2e8f0'
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: '#1a1a2e',
          mb: 4,
          textAlign: 'center'
        }}
      >
        Header & Footer Element Kütüphanesi
      </Typography>

      <Grid container spacing={4}>
        {/* Header Elements */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 4,
              borderRadius: 3,
              border: '2px solid #ec489930',
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.02), transparent)'
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem'
                }}
              >
                📋
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                Header Elementleri
              </Typography>
            </Stack>

            <Grid container spacing={1.5}>
              {[
                'Logo', 'Menü Linki', 'Dropdown Menü', 'Mega Menü',
                'Arama İkonu', 'Arama Kutusu', 'Kullanıcı Profil', 'CTA Butonu',
                'Slogan', 'Breadcrumb', 'Hava Durumu', 'Tarih/Saat',
                'Bildirimler', 'Dil Seçici', 'Tema Değiştirici', 'Sosyal Medya',
                'Ticker', 'Duyuru Bandı', 'HTML İçerik', 'Özel Widget'
              ].map((element, idx) => (
                <Grid item xs={6} key={idx}>
                  <Chip
                    label={element}
                    size="small"
                    sx={{
                      width: '100%',
                      justifyContent: 'flex-start',
                      background: '#f8f9fa',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      '&:hover': {
                        background: '#ec489915',
                        color: '#ec4899'
                      }
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Footer Elements */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 4,
              borderRadius: 3,
              border: '2px solid #06b6d430',
              background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.02), transparent)'
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem'
                }}
              >
                📄
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                Footer Elementleri
              </Typography>
            </Stack>

            <Grid container spacing={1.5}>
              {[
                'Footer Logo', 'Metin Bloğu', 'Link Grubu', 'Menü Listesi',
                'Sosyal Medya', 'İletişim Bilgileri', 'Newsletter', 'Copyright',
                'Yasal Linkler', 'Ödeme Logoları', 'Harita', 'İstatistikler',
                'Marka Logosu', 'Adres Bilgisi', 'HTML İçerik'
              ].map((element, idx) => (
                <Grid item xs={6} key={idx}>
                  <Chip
                    label={element}
                    size="small"
                    sx={{
                      width: '100%',
                      justifyContent: 'flex-start',
                      background: '#f8f9fa',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      '&:hover': {
                        background: '#06b6d415',
                        color: '#06b6d4'
                      }
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  </Container>
</Box>

{/* Tasarım Süreci - Header & Footer */}
<Box
  sx={{
    background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
    py: { xs: 8, md: 12 },
    position: 'relative',
    overflow: 'hidden'
  }}
>
  {/* Dekoratif Blob */}
  <Box
    sx={{
      position: 'absolute',
      bottom: -100,
      right: -100,
      width: 400,
      height: 400,
      background: 'linear-gradient(135deg, #ec4899 0%, #06b6d4 100%)',
      borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
      opacity: 0.1,
      filter: 'blur(80px)',
      animation: 'morph 10s ease-in-out infinite'
    }}
  />

  <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
    <Box sx={{ textAlign: 'center', mb: 8 }}>
      <Typography
        variant="overline"
        sx={{
          color: '#ec4899',
          fontSize: '0.9rem',
          fontWeight: 700,
          letterSpacing: '0.1em'
        }}
      >
        TASARIM SÜRECİ
      </Typography>
      <Typography
        variant="h2"
        sx={{
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          fontWeight: 800,
          color: 'white',
          mb: 2,
          mt: 2
        }}
      >
        Kolay 4 Adım
      </Typography>
    </Box>

    <Grid container spacing={4}>
      {[
        {
          step: '1',
          title: 'Şablon Seç',
          description: 'Hazır header/footer şablonlarından birini seçin',
          icon: '🎨',
          color: '#ec4899'
        },
        {
          step: '2',
          title: 'Element Ekle',
          description: 'Sürükle-bırak ile elementleri yerleştirin',
          icon: '🧩',
          color: '#06b6d4'
        },
        {
          step: '3',
          title: 'Özelleştir',
          description: 'Renk, font, boyut ve stil ayarlarını yapın',
          icon: '✨',
          color: '#8b5cf6'
        },
        {
          step: '4',
          title: 'Yayınla',
          description: 'Tasarımınızı kaydedin ve sitenizde kullanın',
          icon: '🚀',
          color: '#10b981'
        }
      ].map((step, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Box
            sx={{
              textAlign: 'center',
              p: 4,
              borderRadius: 4,
              background: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(20px)',
              border: '2px solid rgba(255,255,255,0.1)',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              position: 'relative',
              '&:hover': {
                transform: 'translateY(-12px)',
                borderColor: step.color,
                boxShadow: `0 20px 40px ${step.color}30`,
                '& .step-emoji': {
                  transform: 'scale(1.3) rotate(10deg)'
                }
              }
            }}
          >
            {/* Step Number */}
            <Box
              sx={{
                position: 'absolute',
                top: -15,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 35,
                height: 35,
                borderRadius: '50%',
                background: step.color,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: '1rem',
                boxShadow: `0 4px 16px ${step.color}60`
              }}
            >
              {step.step}
            </Box>

            {/* Emoji */}
            <Box
              className="step-emoji"
              sx={{
                fontSize: '4rem',
                mb: 2,
                mt: 2,
                transition: 'all 0.3s ease'
              }}
            >
              {step.icon}
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'white',
                mb: 1.5
              }}
            >
              {step.title}
            </Typography>

            <Typography
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.9rem',
                lineHeight: 1.6
              }}
            >
              {step.description}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  </Container>
</Box>


{/* Header & Footer Tasarımcısı Bölümü */}
<Box
  sx={{
    py: { xs: 8, md: 12 },
    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    position: 'relative',
    overflow: 'hidden'
  }}
>
  {/* Dekoratif Blob */}
  <Box
    sx={{
      position: 'absolute',
      bottom: -150,
      left: -150,
      width: 500,
      height: 500,
      background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
      borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
      opacity: 0.05,
      filter: 'blur(80px)'
    }}
  />

  <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
    {/* Section Header */}
    <Box textAlign="center" mb={8}>
      <Chip
        icon={<DesignServices />}
        label="HEADER & FOOTER DESIGNER"
        sx={{
          fontSize: '0.85rem',
          fontWeight: 700,
          background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
          color: 'white',
          mb: 3,
          px: 2,
          py: 2.5
        }}
      />
      
      <Typography
        variant="h2"
        sx={{
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          fontWeight: 800,
          mb: 2,
          color: '#1e293b'
        }}
      >
        Header & Footer Özelleştirme
      </Typography>
      
      <Typography
        variant="h6"
        sx={{
          color: '#64748b',
          maxWidth: '700px',
          mx: 'auto',
          lineHeight: 1.6
        }}
      >
        Sürükle-bırak editör ile header ve footer'ınızı istediğiniz gibi tasarlayın
      </Typography>
    </Box>

    {/* Header Designer */}
    <Box mb={10}>
      <Stack direction="row" spacing={2} alignItems="center" mb={4}>
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography sx={{ fontSize: '2rem' }}>📱</Typography>
        </Box>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: '#1e293b',
              mb: 0.5
            }}
          >
            Header Tasarımcısı
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            Profesyonel header şablonları ile sitenizin üst kısmını özelleştirin
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={4}>
        {/* Header Template 1 - Classic */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '2px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 40px rgba(139, 92, 246, 0.15)',
                borderColor: '#8b5cf6'
              }
            }}
          >
            {/* Header Preview Image */}
            <Box
              sx={{
                height: 200,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Simulated Header Design */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  p: 2,
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                {/* Logo Area */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}
                    />
                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1e293b' }}>
                      HABER SİTESİ
                    </Typography>
                  </Box>
                  
                  {/* Menu Items */}
                  <Stack direction="row" spacing={2} sx={{ display: { xs: 'none', md: 'flex' } }}>
                    {['Ana Sayfa', 'Haberler', 'Gündem', 'Spor'].map((item) => (
                      <Typography
                        key={item}
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: '#64748b',
                          cursor: 'pointer'
                        }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                </Stack>
                
                {/* Category Bar */}
                <Stack
                  direction="row"
                  spacing={1}
                  mt={2}
                  sx={{
                    borderTop: '1px solid #e2e8f0',
                    pt: 1.5
                  }}
                >
                  {['Ekonomi', 'Teknoloji', 'Sağlık', 'Kültür'].map((cat) => (
                    <Chip
                      key={cat}
                      label={cat}
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: '#f1f5f9',
                        '&:hover': { background: '#e2e8f0' }
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </Box>

            {/* Card Content */}
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1} mb={2}>
                <Chip
                  label="Klasik"
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
                <Chip
                  label="Popüler"
                  size="small"
                  sx={{
                    background: '#fef3c7',
                    color: '#f59e0b',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
              </Stack>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 1
                }}
              >
                Klasik Header Tasarımı
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  mb: 3,
                  lineHeight: 1.6
                }}
              >
                Logo, menü ve kategori barı ile geleneksel haber sitesi görünümü
              </Typography>

              {/* Features */}
              <Stack spacing={1.5} mb={3}>
                {[
                  'Sticky header desteği',
                  'Mega menü özelliği',
                  'Arama kutusu entegrasyonu',
                  'Sosyal medya linkleri'
                ].map((feature) => (
                  <Stack key={feature} direction="row" spacing={1} alignItems="center">
                    <CheckIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />
                    <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                      {feature}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Button
                fullWidth
                variant="outlined"
                sx={{
                  borderColor: '#8b5cf6',
                  color: '#8b5cf6',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#7c3aed',
                    background: '#f5f3ff'
                  }
                }}
              >
                Bu Tasarımı Kullan
              </Button>
            </CardContent>
          </Paper>
        </Grid>

        {/* Header Template 2 - Modern */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '2px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 40px rgba(236, 72, 153, 0.15)',
                borderColor: '#ec4899'
              }
            }}
          >
            {/* Header Preview Image */}
            <Box
              sx={{
                height: 200,
                background: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Simulated Modern Header */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  p: 2
                }}
              >
                {/* Top Bar */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    background: 'rgba(15, 23, 42, 0.95)',
                    backdropFilter: 'blur(10px)',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    mb: 2
                  }}
                >
                  <Typography sx={{ fontSize: '0.75rem', color: 'white', fontWeight: 600 }}>
                    🔥 Son Dakika: Önemli Gelişme
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {['📱', '🐦', '📷'].map((icon, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          background: 'rgba(255, 255, 255, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem'
                        }}
                      >
                        {icon}
                      </Box>
                    ))}
                  </Stack>
                </Stack>

                {/* Main Header */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(10px)',
                    px: 2,
                    py: 1.5,
                    borderRadius: 2
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={3}>
                    <Box
                      sx={{
                        width: 45,
                        height: 45,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem'
                      }}
                    >
                      📰
                    </Box>
                    <Stack direction="row" spacing={2} sx={{ display: { xs: 'none', md: 'flex' } }}>
                      {['Gündem', 'Ekonomi', 'Spor', 'Teknoloji'].map((item) => (
                        <Typography
                          key={item}
                          sx={{
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            color: '#1e293b'
                          }}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Stack>
                  </Stack>
                  
                  <Stack direction="row" spacing={1}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        background: '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      🔍
                    </Box>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        background: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      👤
                    </Box>
                  </Stack>
                </Stack>
              </Box>
            </Box>

            {/* Card Content */}
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1} mb={2}>
                <Chip
                  label="Modern"
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
                <Chip
                  label="Yeni"
                  size="small"
                  sx={{
                    background: '#dcfce7',
                    color: '#16a34a',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
              </Stack>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 1
                }}
              >
                Modern Header Tasarımı
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  mb: 3,
                  lineHeight: 1.6
                }}
              >
                Son dakika barı, minimal tasarım ve kullanıcı odaklı modern görünüm
              </Typography>

              {/* Features */}
              <Stack spacing={1.5} mb={3}>
                {[
                  'Son dakika haber barı',
                  'Kullanıcı profil menüsü',
                  'Gelişmiş arama özellikleri',
                  'Dark mode desteği'
                ].map((feature) => (
                  <Stack key={feature} direction="row" spacing={1} alignItems="center">
                    <CheckIcon sx={{ fontSize: 16, color: '#ec4899' }} />
                    <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                      {feature}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Button
                fullWidth
                variant="outlined"
                sx={{
                  borderColor: '#ec4899',
                  color: '#ec4899',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#db2777',
                    background: '#fdf2f8'
                  }
                }}
              >
                Bu Tasarımı Kullan
              </Button>
            </CardContent>
          </Paper>
        </Grid>

        {/* Header Template 3 - Minimalist */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '2px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 40px rgba(59, 130, 246, 0.15)',
                borderColor: '#3b82f6'
              }
            }}
          >
            {/* Header Preview Image */}
            <Box
              sx={{
                height: 200,
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Simulated Minimalist Header */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  p: 3
                }}
              >
                {/* Centered Logo */}
                <Box textAlign="center" mb={2}>
                  <Typography
                    sx={{
                      fontSize: '2rem',
                      fontWeight: 900,
                      color: 'white',
                      letterSpacing: '-0.02em',
                      textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                    }}
                  >
                    HABER
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: 'rgba(255,255,255,0.8)',
                      fontWeight: 600,
                      letterSpacing: '0.1em'
                    }}
                  >
                    GÜNCEL • TARAFSIZ • HIZLI
                  </Typography>
                </Box>

                {/* Centered Menu */}
                <Stack
                  direction="row"
                  spacing={3}
                  justifyContent="center"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    px: 3,
                    py: 1.5,
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  {['Ana Sayfa', 'Gündem', 'Ekonomi', 'Spor', 'Teknoloji'].map((item) => (
                    <Typography
                      key={item}
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: 'white',
                        display: { xs: 'none', md: 'block' }
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Box>

            {/* Card Content */}
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1} mb={2}>
                <Chip
                  label="Minimalist"
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
                <Chip
                  label="Şık"
                  size="small"
                  sx={{
                    background: '#f3e8ff',
                    color: '#9333ea',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
              </Stack>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 1
                }}
              >
                Minimalist Header Tasarımı
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  mb: 3,
                  lineHeight: 1.6
                }}
              >
                Merkezi logo, sade menü ve temiz tasarım anlayışı
              </Typography>

              {/* Features */}
              <Stack spacing={1.5} mb={3}>
                {[
                  'Merkezi logo yerleşimi',
                  'Glassmorphism efekti',
                  'Minimal ve şık görünüm',
                  'Hızlı yükleme süresi'
                ].map((feature) => (
                  <Stack key={feature} direction="row" spacing={1} alignItems="center">
                    <CheckIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                    <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                      {feature}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Button
                fullWidth
                variant="outlined"
                sx={{
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#2563eb',
                    background: '#eff6ff'
                  }
                }}
              >
                Bu Tasarımı Kullan
              </Button>
            </CardContent>
          </Paper>
        </Grid>

        {/* Header Template 4 - Magazine Style */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '2px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 40px rgba(16, 185, 129, 0.15)',
                borderColor: '#10b981'
              }
            }}
          >
            {/* Header Preview Image */}
            <Box
              sx={{
                height: 200,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Simulated Magazine Header */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  p: 2
                }}
              >
                {/* Top Info Bar */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                  sx={{
                    borderBottom: '1px solid rgba(255,255,255,0.2)',
                    pb: 1
                  }}
                >
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                    📅 15 Ekim 2025 • Çarşamba
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                    🌡️ 22°C İstanbul
                  </Typography>
                </Stack>

                {/* Main Header with Sidebar Menu */}
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Stack spacing={0.5}>
                      {['Gündem', 'Ekonomi', 'Spor', 'Kültür'].map((cat) => (
                        <Box
                          key={cat}
                          sx={{
                            background: 'rgba(255,255,255,0.1)',
                            backdropFilter: 'blur(5px)',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            borderLeft: '3px solid white'
                          }}
                        >
                          <Typography sx={{ fontSize: '0.7rem', color: 'white', fontWeight: 700 }}>
                            {cat}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Grid>
                  <Grid item xs={9}>
                    <Box
                      sx={{
                        background: 'rgba(255,255,255,0.98)',
                        borderRadius: 2,
                        p: 2,
                        textAlign: 'center'
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '1.5rem',
                          fontWeight: 900,
                          color: '#1e293b',
                          letterSpacing: '-0.02em',
                          mb: 0.5
                        }}
                      >
                        HABER MAGAZİN
                      </Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>
                        Türkiye'nin En Çok Okunan Haber Sitesi
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Card Content */}
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1} mb={2}>
                <Chip
                  label="Magazin"
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
                <Chip
                  label="Profesyonel"
                  size="small"
                  sx={{
                    background: '#fef3c7',
                    color: '#f59e0b',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
              </Stack>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 1
                }}
              >
                Magazin Stili Header
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  mb: 3,
                  lineHeight: 1.6
                }}
              >
                Yan menü, tarih/hava durumu bilgisi ve klasik dergi görünümü
              </Typography>

              {/* Features */}
              <Stack spacing={1.5} mb={3}>
                {[
                  'Yan kategori menüsü',
                  'Tarih ve hava durumu',
                  'Klasik dergi tasarımı',
                  'Geniş içerik alanı'
                ].map((feature) => (
                  <Stack key={feature} direction="row" spacing={1} alignItems="center">
                    <CheckIcon sx={{ fontSize: 16, color: '#10b981' }} />
                    <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                      {feature}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Button
                fullWidth
                variant="outlined"
                sx={{
                  borderColor: '#10b981',
                  color: '#10b981',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#059669',
                    background: '#f0fdf4'
                  }
                }}
              >
                Bu Tasarımı Kullan
              </Button>
            </CardContent>
          </Paper>
        </Grid>
      </Grid>
    </Box>

    {/* Divider */}
    <Divider sx={{ my: 10 }} />

    {/* Footer Designer */}
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" mb={4}>
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography sx={{ fontSize: '2rem' }}>🔻</Typography>
        </Box>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: '#1e293b',
              mb: 0.5
            }}
          >
            Footer Tasarımcısı
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            Profesyonel footer şablonları ile sitenizin alt kısmını özelleştirin
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={4}>
        {/* Footer Template 1 - Multi Column */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '2px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 40px rgba(139, 92, 246, 0.15)',
                borderColor: '#8b5cf6'
              }
            }}
          >
            {/* Footer Preview */}
            <Box
              sx={{
                height: 220,
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                position: 'relative',
                overflow: 'hidden',
                p: 2
              }}
            >
              {/* Simulated Multi-Column Footer */}
              <Grid container spacing={2}>
                {/* Column 1 - About */}
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: 'white', mb: 1 }}>
                    HAKKIMIZDA
                  </Typography>
                  <Stack spacing={0.5}>
                    {['Biz Kimiz', 'İletişim', 'Reklam'].map((item) => (
                      <Typography key={item} sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)' }}>
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                </Grid>

                {/* Column 2 - Categories */}
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: 'white', mb: 1 }}>
                    KATEGORİLER
                  </Typography>
                  <Stack spacing={0.5}>
                    {['Gündem', 'Ekonomi', 'Spor'].map((item) => (
                      <Typography key={item} sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)' }}>
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                </Grid>

                {/* Column 3 - Legal */}
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: 'white', mb: 1 }}>
                    YASAL
                  </Typography>
                  <Stack spacing={0.5}>
                    {['Gizlilik', 'Kullanım', 'Çerezler'].map((item) => (
                      <Typography key={item} sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)' }}>
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                </Grid>

                {/* Column 4 - Social */}
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: 'white', mb: 1 }}>
                    SOSYAL MEDYA
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    {['📱', '🐦', '📷', '▶️'].map((icon, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.6rem'
                        }}
                      >
                        {icon}
                      </Box>
                    ))}
                  </Stack>
                </Grid>
              </Grid>

              {/* Bottom Bar */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'rgba(0,0,0,0.3)',
                  backdropFilter: 'blur(10px)',
                  px: 2,
                  py: 1,
                  borderTop: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}>
                  © 2025 Haber Sitesi. Tüm hakları saklıdır.
                </Typography>
              </Box>
            </Box>

            {/* Card Content */}
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1} mb={ 2}>
                <Chip
                  label="Çok Sütunlu"
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
                <Chip
                  label="Popüler"
                  size="small"
                  sx={{
                    background: '#fef3c7',
                    color: '#f59e0b',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
              </Stack>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 1
                }}
              >
                Çok Sütunlu Footer
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  mb: 3,
                  lineHeight: 1.6
                }}
              >
                4 sütunlu yapı, sosyal medya linkleri ve telif hakkı bilgisi
              </Typography>

              {/* Features */}
              <Stack spacing={1.5} mb={3}>
                {[
                  '4 sütunlu responsive yapı',
                  'Sosyal medya entegrasyonu',
                  'Yasal linkler bölümü',
                  'Newsletter formu'
                ].map((feature) => (
                  <Stack key={feature} direction="row" spacing={1} alignItems="center">
                    <CheckIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />
                    <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                      {feature}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Button
                fullWidth
                variant="outlined"
                sx={{
                  borderColor: '#8b5cf6',
                  color: '#8b5cf6',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#7c3aed',
                    background: '#f5f3ff'
                  }
                }}
              >
                Bu Tasarımı Kullan
              </Button>
            </CardContent>
          </Paper>
        </Grid>

        {/* Footer Template 2 - Minimal */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '2px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 40px rgba(59, 130, 246, 0.15)',
                borderColor: '#3b82f6'
              }
            }}
          >
            {/* Footer Preview */}
            <Box
              sx={{
                height: 220,
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 3
              }}
            >
              {/* Logo */}
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}
              >
                <Typography sx={{ fontSize: '1.5rem' }}>📰</Typography>
              </Box>

              {/* Menu Links */}
              <Stack direction="row" spacing={2} mb={2}>
                {['Ana Sayfa', 'Hakkımızda', 'İletişim', 'Gizlilik'].map((item) => (
                  <Typography
                    key={item}
                    sx={{
                      fontSize: '0.7rem',
                      color: 'rgba(255,255,255,0.7)',
                      fontWeight: 600
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Stack>

              {/* Social Icons */}
              <Stack direction="row" spacing={1} mb={2}>
                {['📱', '🐦', '📷', '▶️', '💼'].map((icon, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                      '&:hover': {
                        background: 'rgba(59, 130, 246, 0.3)'
                      }
                    }}
                  >
                    {icon}
                  </Box>
                ))}
              </Stack>

              {/* Copyright */}
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: 'rgba(255,255,255,0.5)',
                  textAlign: 'center'
                }}
              >
                © 2025 Haber Sitesi. Tüm hakları saklıdır.
              </Typography>
            </Box>

            {/* Card Content */}
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1} mb={2}>
                <Chip
                  label="Minimal"
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
                <Chip
                  label="Temiz"
                  size="small"
                  sx={{
                    background: '#dcfce7',
                    color: '#16a34a',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
              </Stack>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 1
                }}
              >
                Minimal Footer
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  mb: 3,
                  lineHeight: 1.6
                }}
              >
                Merkezi logo, tek satır menü ve sade sosyal medya ikonları
              </Typography>

              {/* Features */}
              <Stack spacing={1.5} mb={3}>
                {[
                  'Merkezi tasarım',
                  'Tek satır menü yapısı',
                  'Sosyal medya ikonları',
                  'Hızlı yükleme'
                ].map((feature) => (
                  <Stack key={feature} direction="row" spacing={1} alignItems="center">
                    <CheckIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                    <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                      {feature}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Button
                fullWidth
                variant="outlined"
                sx={{
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#2563eb',
                    background: '#eff6ff'
                  }
                }}
              >
                Bu Tasarımı Kullan
              </Button>
            </CardContent>
          </Paper>
        </Grid>
      </Grid>
    </Box>

    {/* CTA Section */}
    <Box
      sx={{
        mt: 10,
        p: 6,
        borderRadius: 4,
        background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          filter: 'blur(60px)'
        }}
      />
      
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: 'white',
          mb: 2,
          position: 'relative',
          zIndex: 2
        }}
      >
        Kendi Tasarımınızı Oluşturun
      </Typography>
      
      <Typography
        variant="body1"
        sx={{
          color: 'rgba(255, 255, 255, 0.9)',
          mb: 4,
          maxWidth: '600px',
          mx: 'auto',
          position: 'relative',
          zIndex: 2
        }}
      >
        Sürükle-bırak editör ile header ve footer'ınızı tamamen özelleştirin. 
        Renk, font, boyut ve düzen ayarlarını istediğiniz gibi yapılandırın.
      </Typography>
      
      <Button
        variant="contained"
        size="large"
        endIcon={<ArrowIcon />}
        sx={{
          background: 'white',
          color: '#8b5cf6',
          px: 6,
          py: 2,
          fontSize: '1.1rem',
          fontWeight: 700,
          borderRadius: 2,
          textTransform: 'none',
          position: 'relative',
          zIndex: 2,
          '&:hover': {
            background: '#f8f9fa',
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
          },
          transition: 'all 0.3s ease'
        }}
      >
        Tasarımcıyı Dene
      </Button>
    </Box>
  </Container>
</Box>

      {/* Özelleştirme Özellikleri */}
      <Box
        sx={{
          background: 'white',
          py: { xs: 8, md: 12 }
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: '#ff6b35',
                fontSize: '0.9rem',
                fontWeight: 700,
                letterSpacing: '0.1em'
              }}
            >
              ÖZELLEŞTİRME SEÇENEKLERİ
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                color: '#1a1a2e',
                mb: 2,
                mt: 2
              }}
            >
              Her Detayı Kontrol Edin
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#6c757d',
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              Renk, font, layout ve daha fazlası - tamamen sizin kontrolünüzde
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {customizationFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  onMouseEnter={() => setHoveredFeature(index)}
                  onMouseLeave={() => setHoveredFeature(null)}
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: '2px solid #e2e8f0',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    '&:hover': {
                      transform: 'translateY(-12px) scale(1.02)',
                      boxShadow: `0 24px 48px ${feature.color}30`,
                      borderColor: feature.color,
                      '& .feature-icon': {
                        transform: 'scale(1.2) rotate(10deg)',
                        background: feature.color,
                        color: 'white'
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 6,
                      background: feature.color,
                      transform: hoveredFeature === index ? 'scaleX(1)' : 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.4s ease'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    {/* Icon */}
                    <Box
                      className="feature-icon"
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 3,
                        background: `${feature.color}15`,
                        color: feature.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '2.5rem',
                        mb: 3,
                        transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        boxShadow: `0 4px 16px ${feature.color}20`
                      }}
                    >
                      {feature.icon}
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: '#1a1a2e',
                        mb: 1.5
                      }}
                    >
                      {feature.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      sx={{
                        color: '#6c757d',
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                        mb: 3
                      }}
                    >
                      {feature.description}
                    </Typography>

                    <Divider sx={{ mb: 3 }} />

                    {/* Details */}
                    <Stack spacing={1.5}>
                      {feature.details.map((detail, idx) => (
                        <Stack key={idx} direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              background: feature.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              flexShrink: 0
                            }}
                          >
                            ✓
                          </Box>
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              color: '#495057',
                              fontWeight: 500
                            }}
                          >
                            {detail}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Tema Yetenekleri */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Grid Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
            opacity: 0.5
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={6} alignItems="center">
            {/* Sol - Görsel */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                  border: '2px solid rgba(255,255,255,0.1)'
                }}
              >
                <img
                  src="https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&h=700&fit=crop"
                  alt="Theme Capabilities"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
                />

                {/* Floating Feature Cards */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 20,
                    left: 20,
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    p: 2,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                    animation: 'float 4s ease-in-out infinite'
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <PaletteIcon />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a2e' }}>
                        Renk Paleti
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#6c757d' }}>
                        Sınırsız kombinasyon
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 20,
                    right: 20,
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    p: 2,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                    animation: 'float 4s ease-in-out infinite 1s'
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontIcon />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a2e' }}>
                        800+ Font
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#6c757d' }}>
                        Google Fonts
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Grid>

            {/* Sağ - Özellikler */}
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{
                  color: '#ff6b35',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em'
                }}
              >
                TEMA YETENEKLERİ
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  fontWeight: 800,
                  color: 'white',
                  mb: 3,
                  mt: 2
                }}
              >
                Gelişmiş Tema{' '}
                <span style={{ color: '#ff6b35' }}>Kontrolleri</span>
              </Typography>

              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '1.05rem',
                  lineHeight: 1.8,
                  mb: 5
                }}
              >
                Profesyonel web tasarımcıları için geliştirilmiş, 
                ancak herkesin kullanabileceği güçlü tema araçları.
              </Typography>

              {/* Capabilities List */}
              <Grid container spacing={3}>
                {themeCapabilities.map((capability, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        border: '2px solid rgba(255,255,255,0.1)',
                        background: 'rgba(255,255,255,0.05)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: capability.color,
                          transform: 'translateX(8px)',
                          boxShadow: `0 8px 24px ${capability.color}30`,
                          '& .capability-icon': {
                            background: capability.color,
                            color: 'white',
                            transform: 'scale(1.1) rotate(5deg)'
                          }
                        }
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="start">
                        <Box
                          className="capability-icon"
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: 2,
                            background: `${capability.color}20`,
                            color: capability.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.5rem',
                            flexShrink: 0,
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {capability.icon}
                        </Box>

                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: 'white',
                              fontSize: '1rem',
                              mb: 0.5
                            }}
                          >
                            {capability.title}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              color: 'rgba(255,255,255,0.7)',
                              lineHeight: 1.5
                            }}
                          >
                            {capability.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Button
                variant="outlined"
                size="large"
                endIcon={<ArrowIcon />}
                sx={{
                  mt: 5,
                  borderColor: '#ff6b35',
                  color: '#ff6b35',
                  px: 5,
                  py: 2,
                  borderRadius: '50px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderWidth: 2,
                  '&:hover': {
                    background: 'rgba(255, 107, 53, 0.1)',
                    borderWidth: 2
                  }
                }}
              >
                Tüm Özellikleri Gör
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Canlı Demo */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          py: { xs: 8, md: 12 }}
        }
      >
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{
                  color: '#ff6b35',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em'
                }}
              >
                CANLI DEMO
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  fontWeight: 800,
                  color: '#1a1a2e',
                  mb: 3,
                  mt: 2
                }}
              >
                Tema Editörünü{' '}
                <span style={{ color: '#ff6b35' }}>Canlı Deneyin</span>
              </Typography>

              <Typography
                sx={{
                  color: '#6c757d',
                  fontSize: '1.05rem',
                  lineHeight: 1.8,
                  mb: 4
                }}
              >
                Değişikliklerinizi anında görün. Renk, font ve layout ayarlarını 
                gerçek zamanlı olarak test edin.
              </Typography>

              {/* Feature Highlights */}
              <Stack spacing={3} mb={4}>
                {[
                  { icon: <PreviewIcon />, title: 'Gerçek Zamanlı Önizleme', value: 'Anında görün' },
                  { icon: <TouchIcon />, title: 'Kolay Kullanım', value: 'Drag & drop' },
                  { icon: <CodeIcon />, title: 'Kod Gerektirmez', value: 'Görsel arayüz' }
                ].map((item, idx) => (
                  <Stack key={idx} direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 55,
                        height: 55,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        boxShadow: '0 4px 16px rgba(255, 107, 53, 0.3)'
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: '1rem' }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ color: '#ff6b35', fontWeight: 700, fontSize: '0.9rem' }}>
                        {item.value}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>

              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                  color: 'white',
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: '50px',
                  textTransform: 'none',
                  boxShadow: '0 8px 32px rgba(255, 107, 53, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #f7931e 0%, #ff6b35 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(255, 107, 53, 0.5)'
                  }
                }}
              >
                Canlı Demo'yu Dene
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Interactive Theme Editor Mockup */}
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                  border: '2px solid rgba(255,255,255,0.1)'
                }}
              >
                {/* Editor Interface */}
                <Box
                  sx={{
                    background: 'white',
                    p: 4
                  }}
                >
                  {/* Color Picker Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontWeight: 700, color: '#1a1a2e', mb: 2, fontSize: '0.95rem' }}>
                      Renk Seçimi
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                      {['#ff6b35', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#f59e0b'].map((color, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            width: 45,
                            height: 45,
                            borderRadius: 2,
                            background: color,
                            cursor: 'pointer',
                            border: '3px solid white',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'scale(1.2)',
                              boxShadow: `0 8px 24px ${color}60`
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  {/* Font Selection */}
                  <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontWeight: 700, color: '#1a1a2e', mb: 2, fontSize: '0.95rem' }}>
                      Font Ailesi
                    </Typography>
                    <Grid container spacing={1.5}>
                      {['Inter', 'Roboto', 'Poppins', 'Montserrat'].map((font, idx) => (
                        <Grid item xs={6} key={idx}>
                          <Paper
                            sx={{
                              p: 1.5,
                              textAlign: 'center',
                              border: '2px solid #e2e8f0',
                              borderRadius: 2,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: '#ff6b35',
                                transform: 'translateY(-2px)'
                              }
                            }}
                          >
                            <Typography sx={{ fontFamily: font, fontWeight: 600, fontSize: '0.85rem' }}>
                              {font}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  {/* Mode Toggle */}
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: '#1a1a2e', mb: 2, fontSize: '0.95rem' }}>
                      Tema Modu
                    </Typography>
                    <ToggleButtonGroup
                      value={themeMode}
                      exclusive
                      onChange={(e, newMode) => newMode && setThemeMode(newMode)}
                      fullWidth
                      sx={{
                        '& .MuiToggleButton-root': {
                          py: 1.5,
                          borderRadius: '50px',
                          textTransform: 'none',
                          fontWeight: 600,
                          border: '2px solid #e2e8f0',
                          '&.Mui-selected': {
                            background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                            color: 'white',
                            border: '2px solid #ff6b35',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #f7931e 0%, #ff6b35 100%)'
                            }
                          }
                        }
                      }}
                    >
                      <ToggleButton value="light">
                        <LightIcon sx={{ mr: 1 }} /> Açık
                      </ToggleButton>
                      <ToggleButton value="dark">
                        <DarkIcon sx={{ mr: 1 }} /> Koyu
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>

                {/* Live Badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 15,
                    right: 15,
                    background: 'rgba(16, 185, 129, 0.95)',
                    color: 'white',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    animation: 'pulse 2s ease-in-out infinite',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'white',
                      animation: 'pulse 1s ease-in-out infinite'
                    }}
                  />
                  CANLI EDİTÖR
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #3b82f6 100%)',
          py: { xs: 8, md: 10 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Animated Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)',
            backgroundSize: '30px 30px',
            opacity: 0.6,
            animation: 'patternMove 20s linear infinite'
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 90,
                height: 90,
                borderRadius: '50%',
                background: 'white',
                color: '#ff6b35',
                fontSize: '3rem',
                mb: 3,
                boxShadow: '0 12px 40px rgba(0,0,0,0.3)'
              }}
            >
              <MagicIcon fontSize="large" />
            </Box>

            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 900,
                color: 'white',
                mb: 3,
                textShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}
            >
              Markanızı Yansıtın
            </Typography>

            <Typography
              variant="h5"
              sx={{
                color: 'rgba(255,255,255,0.95)',
                mb: 5,
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6,
                textShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              30 gün ücretsiz deneme ile tema editörünü keşfedin. 
              Sınırsız özelleştirme, sınırsız yaratıcılık.
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              sx={{ justifyContent: 'center', mb: 5 }}
            >
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowIcon />}
                sx={{
                  background: 'white',
                  color: '#ff6b35',
                  px: 6,
                  py: 2.5,
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  borderRadius: '50px',
                  textTransform: 'none',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  '&:hover': {
                    background: '#f8f9fa',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.4)'
                  }
                }}
              >
                Ücretsiz Başla
              </Button>

              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 6,
                  py: 2.5,
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  borderRadius: '50px',
                  textTransform: 'none',
                  borderWidth: 3,
                  '&:hover': {
                    background: 'rgba(255,255,255,0.15)',
                    borderColor: 'white',
                    borderWidth: 3
                  }
                }}
              >
                Şablonları Gör
              </Button>
            </Stack>

            {/* Trust Badges */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={4}
              sx={{ justifyContent: 'center', alignItems: 'center' }}
            >
              {[
                '✓ Kod bilgisi gerektirmez',
                '✓ 6 hazır şablon',
                '✓ Sınırsız özelleştirme'
              ].map((text, idx) => (
                <Typography
                  key={idx}
                  sx={{
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: 700,
                    textShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}
                >
                  {text}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes morph {
          0%, 100% {
            border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%;
          }
          25% {
            border-radius: 60% 40% 50% 50% / 30% 60% 40% 60%;
          }
          50% {
            border-radius: 50% 50% 30% 70% / 50% 30% 70% 40%;
          }
          75% {
            border-radius: 30% 70% 60% 40% / 60% 40% 50% 50%;
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }

        @keyframes patternMove {
          0% {
            background-position: 0 0;
          }
          100% {
            background-position: 30px 30px;
          }
        }
      `}</style>
    </Box>
  );
};

export default ThemeDesignSystemPage;